import { motion } from "framer-motion";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useRef, useState, useEffect } from "react";
import { useInView } from "framer-motion";

// Custom hook for scroll-triggered animations with mobile optimization
function useScrollAnimation() {
  const ref = useRef(null);
  // Use different thresholds for mobile vs desktop
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Use smaller threshold for mobile (10% vs 30% for desktop)
  const isInView = useInView(ref, { 
    once: false, 
    amount: isMobile ? 0.1 : 0.3 
  });
  return { ref, isInView };
}

export default function WebDevelopment() {
  const heroRef = useScrollAnimation();
  const technologiesRef = useScrollAnimation();
  const featuresRef = useScrollAnimation();
  const processRef = useScrollAnimation();
  const servicesRef = useScrollAnimation();
  const ctaRef = useScrollAnimation();

  const technologies = [
    { 
      name: "React.js", 
      icon: "fab fa-react", 
      color: "from-cyan-400 to-blue-500",
      description: "Modern UI library"
    },
    { 
      name: "Node.js", 
      icon: "fab fa-node-js", 
      color: "from-green-400 to-emerald-500",
      description: "Server-side JavaScript"
    },
    { 
      name: "ASP.NET", 
      icon: "fab fa-microsoft", 
      color: "from-blue-500 to-purple-500",
      description: "Enterprise framework"
    },
    { 
      name: "PHP", 
      icon: "fab fa-php", 
      color: "from-purple-400 to-pink-500",
      description: "Server-side scripting"
    },
    { 
      name: "Python", 
      icon: "fab fa-python", 
      color: "from-yellow-400 to-orange-500",
      description: "Versatile backend"
    },
    { 
      name: "MongoDB", 
      icon: "fas fa-database", 
      color: "from-green-500 to-teal-400",
      description: "NoSQL database"
    },
    { 
      name: "MySQL", 
      icon: "fas fa-database", 
      color: "from-orange-400 to-red-500",
      description: "Relational database"
    },
    { 
      name: "PostgreSQL", 
      icon: "fas fa-database", 
      color: "from-blue-600 to-indigo-500",
      description: "Advanced database"
    }
  ];

  const features = [
    {
      title: "Custom Development",
      description: "Tailored web applications for your business requirements",
      icon: "fas fa-code",
      gradient: "from-blue-500 to-cyan-400"
    },
    {
      title: "Responsive Design",
      description: "Perfect view on all devices with responsive design",
      icon: "fas fa-mobile-alt",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      title: "Performance", 
      description: "Fast-loading, efficient code for exceptional UX",
      icon: "fas fa-bolt",
      gradient: "from-green-500 to-emerald-400"
    },
    {
      title: "Security Focus",
      description: "Built-in security to protect your data",
      icon: "fas fa-shield-alt",
      gradient: "from-orange-500 to-yellow-400"
    },
    {
      title: "Scalable",
      description: "Future-ready solutions that grow with you",
      icon: "fas fa-expand-arrows-alt",
      gradient: "from-red-500 to-orange-400"
    },
    {
      title: "API Integration",
      description: "Seamless integration with third-party services",
      icon: "fas fa-plug",
      gradient: "from-indigo-500 to-purple-400"
    }
  ];

  const developmentServices = [
    {
      title: "Frontend Development",
      description: "Modern, responsive interfaces using React, Vue.js, and Angular",
      features: ["SPA Development", "PWA Implementation", "UI/UX Integration"],
      icon: "fas fa-desktop",
      gradient: "from-blue-500 to-cyan-400"
    },
    {
      title: "Backend Development",
      description: "Robust server-side solutions with Node.js, Python, and .NET",
      features: ["REST APIs", "Microservices", "Database Design"],
      icon: "fas fa-server",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      title: "Full Stack",
      description: "End-to-end web application development",
      features: ["MERN Stack", "MEAN Stack", "Complete Integration"],
      icon: "fas fa-layer-group",
      gradient: "from-green-500 to-emerald-400"
    },
    {
      title: "E-commerce",
      description: "Custom online stores with advanced features",
      features: ["Payment Gateway", "Inventory Management", "Order Processing"],
      icon: "fas fa-shopping-cart",
      gradient: "from-orange-500 to-yellow-400"
    }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden" style={{ fontFamily: "'Roboto', sans-serif" }}>
      <Navigation />
      
      {/* Hero Section - Optimized for Mobile */}
      <section
        ref={heroRef.ref}
        className="relative flex items-center justify-center overflow-hidden bg-[#040814] text-white pt-28 pb-10 md:pb-12"
      >
        {/* Simplified Background for Mobile */}
        <div className="absolute inset-0">
          {/* Reduced blobs for mobile */}
          <div className="absolute -top-40 md:-top-60 left-1/4 w-[300px] h-[300px] md:w-[650px] md:h-[650px] bg-gradient-to-r from-cyan-500/20 to-blue-700/20 rounded-full blur-[100px] md:blur-[180px] animate-pulse opacity-20" />
          <div className="absolute top-1/4 right-0 md:left-1/2 w-[250px] h-[250px] md:w-[700px] md:h-[700px] bg-gradient-to-r from-teal-500/20 to-purple-600/20 rounded-full blur-[80px] md:blur-[180px] animate-pulse opacity-15" />
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={heroRef.isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="text-center"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={heroRef.isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-xl mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-xs md:text-sm tracking-wide text-cyan-300 font-semibold">
                WEB DEVELOPMENT SERVICES
              </span>
            </motion.div>

            {/* Heading - Optimized for Mobile */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={heroRef.isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4 px-2"
            > 
              Build Powerful   
              <span className="block bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent mt-2">
                Web Experiences
              </span>
            </motion.h1>

            {/* Description - Optimized for Mobile */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={heroRef.isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
              className="mt-4 text-sm sm:text-base md:text-lg text-white/70 max-w-2xl mx-auto px-4 leading-relaxed"
            >
              We design and develop high-performance, scalable, and responsive web applications
              that look stunning on every screen and drive real business growth.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Technologies Section - Optimized for Mobile */}
      <section ref={technologiesRef.ref} className="py-12 md:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={technologiesRef.isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10 md:mb-16 px-2"
          >
            <span className="inline-block px-3 py-1 md:px-4 md:py-2 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-600 rounded-full text-xs md:text-sm font-semibold mb-3">
              OUR TECH STACK
            </span>
            <h2 className="text-xl sm:text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Technologies We <span className="text-blue-600">Master</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              We leverage the latest technologies to build robust, scalable web applications.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6 px-2">
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={technologiesRef.isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ y: -5 }}
                className="group"
              >
                <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-6 border border-gray-100 shadow-sm md:shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                  <div className={`w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-r ${tech.color} flex items-center justify-center mb-3`}>
                    <i className={`${tech.icon} text-white text-lg md:text-2xl`}></i>
                  </div>
                  <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1">
                    {tech.name}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500">
                    {tech.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section - Optimized for Mobile */}
      <section ref={featuresRef.ref} className="py-12 md:py-20 bg-gradient-to-b from-gray-900 to-[#040814]">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={featuresRef.isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10 md:mb-16 px-2"
          >
            <span className="inline-block px-3 py-1 md:px-4 md:py-2 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 text-cyan-400 rounded-full text-xs md:text-sm font-semibold mb-3">
              OUR FEATURES
            </span>
            <h2 className="text-xl sm:text-2xl md:text-4xl font-bold text-white mb-4">
              Development <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Features</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
              Our web solutions are built with modern best practices.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8 px-2">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={featuresRef.isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                whileHover={{ y: -5 }}
                className="group"
              >
                <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 rounded-xl md:rounded-2xl p-5 md:p-8 border border-gray-700 hover:border-cyan-500/30 transition-all duration-300 h-full">
                  <div className={`w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl bg-gradient-to-r ${feature.gradient} flex items-center justify-center mb-4`}>
                    <i className={`${feature.icon} text-white text-lg md:text-2xl`}></i>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - Added for Mobile */}
      <section ref={servicesRef.ref} className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={servicesRef.isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10 md:mb-16 px-2"
          >
            <span className="inline-block px-3 py-1 md:px-4 md:py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-600 rounded-full text-xs md:text-sm font-semibold mb-3">
              OUR SERVICES
            </span>
            <h2 className="text-xl sm:text-2xl md:text-4xl font-bold text-gray-900 mb-4">
              Development <span className="text-purple-600">Services</span>
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive web development services tailored to your needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-8 px-2">
            {developmentServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesRef.isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group"
              >
                <div className="bg-white rounded-xl md:rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm md:shadow-lg hover:shadow-xl transition-all duration-300 h-full">
                  <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-r ${service.gradient} flex items-center justify-center mb-4`}>
                    <i className={`${service.icon} text-white text-lg md:text-2xl`}></i>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 mb-4">
                    {service.description}
                  </p>
                  <ul className="space-y-1">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-xs md:text-sm text-gray-500">
                        <i className="fas fa-check text-green-500 mr-2 text-xs"></i>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section - Optimized for Mobile */}
      <section ref={ctaRef.ref} className="py-12 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={ctaRef.isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            {/* Heading */}
            <h2 className="text-xl sm:text-2xl md:text-4xl font-bold mb-4 leading-tight text-gray-900 px-2">
              Ready to Build Your <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Digital Solution</span>?
            </h2>

            {/* Description */}
            <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-8 md:mb-12 max-w-xl mx-auto px-4">
              Let's create a powerful, responsive web application that drives your business forward. 
              Contact us today for a free consultation.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center px-4">
              <Link href="/getintouch" className="w-full sm:w-auto">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full px-6 py-3 md:px-10 md:py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg md:rounded-xl font-semibold text-sm md:text-base shadow-lg shadow-blue-500/30 transition-all duration-300 flex items-center justify-center gap-3"
                >
                  <i className="fas fa-calendar-alt"></i>
                  Schedule Free Call
                </motion.button>
              </Link>
            </div>
            
            {/* Additional Info */}
            <p className="mt-6 text-xs md:text-sm text-gray-500">
              Response within 24 hours • No obligation
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}